import type { MutateSelectedFailure } from '../../hooks/useBillingCycles';
import type { Toast } from '../../store/slices/toastSlice';
import {
  getBusinessErrorMessage,
  getSurfaceableError,
} from '../../utils/apiErrorDetail';

export type ToastLinkAction = NonNullable<Toast['linkAction']>;

type ShowErrorFn = (
  title: string,
  message: string,
  linkAction?: ToastLinkAction
) => void;

const CREATE_CLAIM_FALLBACK = 'Failed to create claim';

/** Cap the per-claim lines so a large batch failure stays a readable toast. */
const MAX_DETAILED_LINES = 5;

/** The backend's `error_code` for an insurance with no linked payer. */
export const INSURANCE_MISSING_PAYER_CODE = 'insurance_missing_payer';

/**
 * Each distinct insurance a failure blamed for having no payer: its id, and
 * its name when the backend had one.
 */
const insurancesMissingPayer = (failures: MutateSelectedFailure[]) => {
  const insurances = new Map<string, string | null>();
  for (const failure of failures) {
    const surfaceable = getSurfaceableError(failure.error);
    const id = surfaceable?.data.agency_insurance_id;
    const name = surfaceable?.data.insurance_name;
    if (
      surfaceable?.errorCode === INSURANCE_MISSING_PAYER_CODE &&
      typeof id === 'string'
    ) {
      insurances.set(id, typeof name === 'string' && name ? name : null);
    }
  }
  return insurances;
};

/**
 * What a user who cannot edit insurances should do about a missing payer:
 * the failure lines tell them to pick the payer, which they cannot do.
 */
const askAnAdminLine = (
  failures: MutateSelectedFailure[]
): string | undefined => {
  const names = [...insurancesMissingPayer(failures).values()];
  if (names.length === 0) return undefined;
  if (names.length === 1) {
    return `Ask an admin to link ${names[0] ?? 'this insurance'} to a payer.`;
  }
  const named = names.filter((name): name is string => name !== null);
  return named.length === names.length
    ? `Ask an admin to link ${named.join(', ')} to their payers.`
    : 'Ask an admin to link these insurances to their payers.';
};

/**
 * Where the toast sends a biller whose claims failed for want of a linked
 * payer: that insurance's edit window, or the insurance list when several
 * insurances are to blame.
 */
const fixInsuranceLink = (
  failures: MutateSelectedFailure[]
): ToastLinkAction | undefined => {
  const ids = [...insurancesMissingPayer(failures).keys()];
  if (ids.length === 0) return undefined;
  if (ids.length === 1) {
    const params = new URLSearchParams({
      section: 'insurance',
      editInsurance: ids[0],
    });
    return {
      label: 'Open insurance',
      altText: 'Open the insurance to link its payer',
      href: `/admin?${params.toString()}`,
    };
  }
  return {
    label: 'Open insurances',
    altText: 'Open the insurance list to link their payers',
    href: '/admin?section=insurance',
  };
};

/**
 * Toast the per-cycle reasons a Create Claim batch failed.
 *
 * Unlike the send flow (whose 4xx details already name the patient), the
 * create-claim 400s describe only the chart problem — e.g. "Primary diagnosis
 * R69 cannot be grouped: …" or "No eligible payment OASIS found for billing
 * cycle" — so every line leads with the patient so the biller knows which row
 * to fix. Only 4xx business details are surfaced (via getBusinessErrorMessage);
 * anything else falls back to generic copy with the MRN for identification.
 * A user who can edit insurances also gets a link to fix a missing payer;
 * anyone else is told to ask an admin.
 */
export const showCreateClaimFailures = (
  failures: MutateSelectedFailure[],
  showError: ShowErrorFn,
  { canEditInsurance = false }: { canEditInsurance?: boolean } = {}
) => {
  const lines = failures.slice(0, MAX_DETAILED_LINES).map(failure => {
    const reason =
      getBusinessErrorMessage(failure.error) ?? CREATE_CLAIM_FALLBACK;
    const identity =
      reason === CREATE_CLAIM_FALLBACK && failure.mrn
        ? `${failure.name} (MRN ${failure.mrn})`
        : failure.name;
    return `• ${identity}: ${reason}`;
  });
  const remaining = failures.length - MAX_DETAILED_LINES;
  if (remaining > 0) {
    lines.push(`• …and ${remaining} more`);
  }

  const linkAction = canEditInsurance ? fixInsuranceLink(failures) : undefined;
  const askAnAdmin = canEditInsurance ? undefined : askAnAdminLine(failures);
  if (askAnAdmin) {
    lines.push(askAnAdmin);
  }
  showError('Some claims could not be created', lines.join('\n'), linkAction);
};
